// v2/components/FitmentInfo.js — gercek arac uyumlulugu (/api/v2/catalog/fitment/<kod>/)
//   FitmentInfo  : detay penceresi — marka logolu, model listeli tam gorunum
//   FitmentLogos : kompakt uyumlu marka logo seridi (QuickView, tam ekran galeri)
//   FitmentLine  : tek satir metin ozeti
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, ArrowRight, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiGet } from '../utils/api';
import BrandLogo from './BrandLogo';
import styles from './FitmentInfo.module.css';

const onbellek = new Map();
const bekleyen = new Map();   // ayni kod icin es zamanli istekler tek istege iner
const ONBELLEK_MAX = 200;
const MODEL_LIMIT = 6;
const MARKA_LIMIT = 8;

function fitmentGetir(kod) {
  if (bekleyen.has(kod)) return bekleyen.get(kod);
  const p = apiGet(`/v2/catalog/fitment/${encodeURIComponent(kod)}/`, {})
    .then((v) => {
      if (onbellek.size >= ONBELLEK_MAX) onbellek.delete(onbellek.keys().next().value);
      onbellek.set(kod, v);
      return v;
    })
    .finally(() => { bekleyen.delete(kod); });
  bekleyen.set(kod, p);
  return p;
}

export function useFitment(code) {
  const kod = (code || '').trim();
  const [state, setState] = useState(() => ({
    data: onbellek.get(kod) || null,
    loading: Boolean(kod) && !onbellek.has(kod),
    error: null,
  }));

  useEffect(() => {
    if (!kod) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    if (onbellek.has(kod)) {
      setState({ data: onbellek.get(kod), loading: false, error: null });
      return undefined;
    }
    let iptal = false;
    setState({ data: null, loading: true, error: null });
    fitmentGetir(kod)
      .then((v) => { if (!iptal) setState({ data: v, loading: false, error: null }); })
      .catch((e) => {
        if (!iptal) setState({ data: null, loading: false, error: (e && e.message) || 'Alınamadı' });
      });
    return () => { iptal = true; };
  }, [kod]);

  return state;
}

const dizi = (v) => (Array.isArray(v) ? v : []);

/** Uretimden kalkma / ikame uyarisi. Kaldirilan urunun stogu olabilir,
 *  bu yuzden "satilamaz" degil "stoklarla sinirli" denir. */
function DurumNotu({ data }) {
  const durum = data && data.status;
  if (durum !== 'kaldirildi' && durum !== 'ikame') return null;
  const kod = (data.successor || '').trim();
  const varMi = Boolean(data.successor_available);
  return (
    <div className={styles.durum}>
      <AlertTriangle size={15} className={styles.durumIcon} />
      <span className={styles.durumText}>
        <b>Üretimden kaldırıldı.</b> Mevcut stoklarla sınırlıdır.
        {kod && (
          <> Yerine geçen ürün: {varMi
            ? <Link className={styles.durumLink} to={`/v2/products?q=${encodeURIComponent(kod)}`}>
                {kod} <ArrowRight size={12} />
              </Link>
            : <b className={styles.durumKod}>{kod}</b>}
          </>
        )}
      </span>
    </div>
  );
}

export default function FitmentInfo({ code }) {
  const { data, loading, error } = useFitment(code);
  const [acik, setAcik] = useState({});
  const [tumMarkalar, setTumMarkalar] = useState(false);
  const [refAcik, setRefAcik] = useState(false);

  useEffect(() => {
    setAcik({});
    setTumMarkalar(false);
    setRefAcik(false);
  }, [code]);

  if (loading) return <span className={styles.muted}>Yükleniyor…</span>;
  if (error) return <span className={styles.muted}>Uyumluluk bilgisi alınamadı.</span>;

  const brands = dizi(data && data.brands);
  const segs = dizi(data && data.segments);
  const engs = dizi(data && data.engines);
  const refs = dizi(data && data.references);
  const eskiler = dizi(data && data.old_codes);
  const durumVar = data && (data.status === 'kaldirildi' || data.status === 'ikame');

  if (!brands.length && !segs.length && !engs.length && !refs.length
      && !eskiler.length && !durumVar) {
    return <span className={styles.muted}>Kayıt yok</span>;
  }

  const gorunen = tumMarkalar ? brands : brands.slice(0, MARKA_LIMIT);

  return (
    <div className={styles.wrap}>
      <DurumNotu data={data} />

      {(segs.length > 0 || engs.length > 0) && (
        <div className={styles.tags}>
          {segs.map((s) => <span key={`s-${s}`} className={styles.tagSeg}>{s}</span>)}
          {engs.map((s) => <span key={`e-${s}`} className={styles.tagEng}>{s}</span>)}
        </div>
      )}

      {gorunen.map((b) => {
        const models = dizi(b.models);
        const acikMi = Boolean(acik[b.id]);
        const liste = acikMi ? models : models.slice(0, MODEL_LIMIT);
        const kalan = models.length - liste.length;
        return (
          <div key={b.id} className={styles.brand}>
            <div className={styles.brandHead}>
              <BrandLogo id={b.id} name={b.name} size="xs" className={styles.logoChip} />
              <b>{b.name}</b>
              {models.length > 0 && <span className={styles.count}>{models.length} model</span>}
            </div>
            {models.length > 0 && (
              <div className={styles.models}>
                {liste.map((m) => <span key={m} className={styles.model}>{m}</span>)}
                {kalan > 0 && (
                  <button type="button" className={styles.more}
                          onClick={() => setAcik((a) => ({ ...a, [b.id]: true }))}>
                    +{kalan} model
                  </button>
                )}
                {acikMi && models.length > MODEL_LIMIT && (
                  <button type="button" className={styles.more}
                          onClick={() => setAcik((a) => ({ ...a, [b.id]: false }))}>
                    Daralt
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {brands.length > MARKA_LIMIT && (
        <button type="button" className={styles.moreBrands}
                onClick={() => setTumMarkalar((t) => !t)}>
          {tumMarkalar
            ? <><ChevronUp size={13} /> Daralt</>
            : <><ChevronDown size={13} /> {brands.length - MARKA_LIMIT} marka daha</>}
        </button>
      )}

      {eskiler.length > 0 && (
        <div className={styles.eski}>
          <span className={styles.eskiBaslik}>
            <History size={13} /> Eski Ürün Kodları
          </span>
          <div className={styles.eskiList}>
            {eskiler.map((k) => <span key={k} className={styles.eskiKod}>{k}</span>)}
          </div>
          <span className={styles.eskiNot}>Bu kodlarla da arama yapabilirsiniz.</span>
        </div>
      )}

      {refs.length > 0 && (
        <div className={styles.refs}>
          <button type="button" className={styles.refToggle} aria-expanded={refAcik}
                  onClick={() => setRefAcik((v) => !v)}>
            Orijinal Referanslar ({refs.length})
            {refAcik ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {refAcik && (
            <div className={styles.refList}>
              {refs.map((r) => <span key={r} className={styles.ref}>{r}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Kompakt uyumlu marka logo seridi.
 *  limit: gosterilecek logo sayisi, kalanlar "+N" olur (uzerine gelince isimler).
 *  koyu : koyu zemin (tam ekran galeri) icin renkler. */
export function FitmentLogos({ code, limit = 6, size = 'sm', koyu = false, className = '' }) {
  const { data, loading, error } = useFitment(code);
  const durum = `${styles.muted} ${koyu ? styles.mutedDark : ''}`;

  if (loading) return <span className={durum}>Yükleniyor…</span>;
  if (error) return <span className={durum}>Uyumluluk bilgisi alınamadı.</span>;

  const brands = dizi(data && data.brands);
  if (!brands.length) return <span className={durum}>Kayıt bulunmuyor</span>;

  const ilk = brands.slice(0, limit);
  const kalan = brands.length - ilk.length;

  return (
    <div className={`${styles.logos} ${koyu ? styles.logosDark : ''} ${className}`}>
      {ilk.map((b) => {
        const m = dizi(b.models);
        const ipucu = m.length
          ? `${b.name}: ${m.slice(0, 8).join(', ')}${m.length > 8 ? ` +${m.length - 8} model` : ''}`
          : b.name;
        return (
          <BrandLogo key={b.id} id={b.id} name={b.name} size={size}
                     title={ipucu} alt={b.name} className={styles.logoChip} />
        );
      })}
      {kalan > 0 && (
        <span className={styles.logoMore}
              title={brands.slice(limit).map((b) => b.name).join(', ')}>
          +{kalan} marka
        </span>
      )}
    </div>
  );
}

/** Hizli bakis icin tek satir ozet */
export function FitmentLine({ code, className }) {
  const { data, loading } = useFitment(code);
  if (loading) return <span className={className}>Yükleniyor…</span>;
  const brands = dizi(data && data.brands);
  if (!brands.length) return <span className={className}>Kayıt bulunmuyor</span>;
  const ilk = brands.slice(0, 2);
  const metin = ilk
    .map((b) => (dizi(b.models).length ? `${b.name} ${b.models[0]}` : b.name))
    .join(' · ');
  const toplam = brands.reduce((s, b) => s + Math.max(dizi(b.models).length, 1), 0);
  const kalan = toplam - ilk.length;
  return <span className={className}>{metin}{kalan > 0 && ` +${kalan} araç`}</span>;
}
